import { createFileRoute } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/Sections";

const URL = "https://finendra.com/privacy";
const OG = "https://finendra.com/og/og-default.png";

const sections = [
  {
    h: "Information we collect",
    p: [
      "Contact details you submit through our contact, proposal, or quote forms — typically your name, company, email address, phone number, and the description of the work you need.",
      "Quote and estimate inputs you enter into our pricing calculator, such as monthly hours and the service scope you selected, so we can prepare and re-open your quote.",
      "Standard technical information generated when you browse the site, including pages visited and general device or browser information.",
    ],
  },
  {
    h: "How we use your information",
    p: [
      "To respond to your enquiry, prepare proposals and quotes, and deliver the services you engage us for.",
      "To operate and improve this website and our service offering.",
      "To meet our legal, accounting, and record-keeping obligations.",
      "We do not sell your personal information, and we do not share it for third-party advertising.",
    ],
  },
  {
    h: "Client financial data",
    p: [
      "When you become a client, we access your accounting systems and financial records only to perform the work in your engagement scope. That data belongs to you.",
      "Access is granted on a least-privilege basis, limited to the team members assigned to your account, and revoked when an engagement ends or a team member changes roles.",
      "We do not use client financial data for any purpose other than delivering your engagement.",
    ],
  },
  {
    h: "Service providers",
    p: [
      "We rely on third-party providers to host this website, store form submissions, and run our internal business tools. These providers process data on our instructions only.",
      "Where an engagement requires us to work inside your accounting stack — for example QuickBooks, Xero, or a payroll platform — that data remains under your account and your provider's terms.",
    ],
  },
  {
    h: "Retention",
    p: [
      "Enquiry and quote records are retained while there is an active business relationship or a reasonable prospect of one, and afterwards only as long as needed for legal and accounting requirements.",
      "You can ask us to delete an enquiry or quote record at any time by emailing connect@finendra.com.",
    ],
  },
  {
    h: "Your choices",
    p: [
      "You can request access to, correction of, or deletion of the personal information you have submitted to us.",
      "You can ask us to stop contacting you at any time, and we will keep only what is required to honour that request.",
      "To make any of these requests, email connect@finendra.com and we will respond as promptly as we reasonably can.",
    ],
  },
  {
    h: "Cookies",
    p: [
      "This site uses only the cookies and local browser storage needed for core functionality, such as keeping you signed in to an administrative session and remembering form state.",
      "You can block or clear cookies in your browser settings; core pages of the site will continue to work.",
    ],
  },
  {
    h: "Changes and contact",
    p: [
      "We may update this policy as our services or providers change, and the revised version will be posted on this page.",
      "Questions about this policy can be sent to connect@finendra.com.",
    ],
  },
];

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy | Finendra" },
      {
        name: "description",
        content:
          "How Finendra collects, uses, retains, and protects the information you submit through this site, and how we handle client financial data.",
      },
      { property: "og:title", content: "Privacy Policy | Finendra" },
      {
        property: "og:description",
        content:
          "Our approach to personal information, client financial data, retention, and your privacy requests.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG },
      { name: "twitter:image", content: OG },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <>
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        body="This page is maintained by Finendra and explains what we collect through this website, how we use it, and how we treat client financial data."
      />

      <section className="px-6 pb-28">
        <div className="mx-auto max-w-3xl space-y-12">
          {sections.map((s, i) => (
            <Reveal key={s.h} delay={i * 0.03}>
              <h2 className="font-display text-2xl font-bold text-brand-navy">{s.h}</h2>
              <div className="mt-4 space-y-4 leading-relaxed text-brand-navy/65">
                {s.p.map((para) => (
                  <p key={para}>{para}</p>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
